<template lang="pug">
div.pt-0.pt-md-3.pb-8
  v-row.pb-2.pb-md-0(no-gutters)
    v-col(cols="12" md="6")
      v-tabs.mb-2.mb-md-0(v-model="categoryTab" color="basic700" height="44" @change="tabChanged")
        template(v-for="category in categories")
          v-tab.text-sm.font-normal.text-none(active-class="font-semibold") {{ category.name }}

    v-col(cols="12" md="6" align="end")
      .d-flex.align-center
        v-spacer
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat :placeholder="$t('type_to_search')" hide-details clearable autofocus)
        v-menu(offset-y left rounded="lg" min-width=200)
          template(v-slot:activator="{ on }")
            v-btn.button-sm.ml-2(color="neutral700" outlined depressed v-on="on") {{ $t('action') }}
              v-icon(right) mdi-chevron-down
          v-list.text-sm.py-0
            v-list-item(@click="createNewCategory")
              .text-none {{ $t('create_new_category') | capitalize }}
            v-divider.basic300
            v-list-item(v-if="userCategories.length" @click="sortCategory")
              div {{ $t('sort_category') }}
            v-list-item(v-else)
              .basic500--text {{ $t('sort_category') }}
            template(v-if="selectedCategory && !selectedCategory.isDefault")
              v-divider
              v-list-item(@click="showRenameDialog = true")
                div {{ $t('rename') | capitalize }} "{{ selectedCategory.name }}"
              v-divider.basic300
              v-list-item(@click="showAssignCategoryDialog = true")
                div {{ $t('assign_more_services_to') }} "{{ selectedCategory.name }}"
              v-divider.basic300
              v-list-item(@click="showDeleteDialog = true")
                .danger--text {{ $t('delete') }} "{{ selectedCategory.name }}"
        v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="addNewService") {{ $t('new_service') }}
  v-divider

  .service-list
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    div.mt-8(v-else)
      draggable(handle=".handle" :list="services" @start="drag = true" @end="endMove" :move="checkMove")
        template(v-for="service in services")
          manage-service-item(:service="service" :company="company" :key="service.id" :drag.sync="drag" @deleted="fetchServices" @edited="fetchServices")

      v-card.round-xl.text-center.pa-8.border-dashed(v-if="categoryTab === 0 && !services.length" outlined)
        v-avatar.mb-8(color="neutral300" size="54" rounded="circle")
          v-icon(color="neutral500" size="36") $yzi-medical-cross
        .mb-1.font-semibold.text-md {{ $t('empty_services') }}
        .text-sm.mb-8.neutral500--text {{ $t('empty_services_desc') }}
        v-btn.button-sm(outlined @click="addNewService") {{ $t('create_a_service') }}

      v-card.round-xl.px-5.mt-3.border-dashed(link outlined height="86" @click="addNewService" v-else)
        div.d-flex.align-center.fill-height
          v-avatar(color="neutral300" size="32")
            v-icon(color="neutral500" size="18") $yzi-plus
          div.text-md.font-semibold.neutral700--text.ml-4 {{ $t('add_service') }}

  .dialogs
    create-category-dialog(v-if="showCreateDialog" :open.sync="showCreateDialog" @created="reset")
    sort-category-dialog(v-if="showSortDialog" :open.sync="showSortDialog" :categories="userCategories" @sorted="reset")
    rename-category-dialog(v-if="showRenameDialog" :open.sync="showRenameDialog" :category="selectedCategory" @edited="reset")
    delete-category-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :category="selectedCategory" @deleted="reset")
    assign-category-services-dialog(v-if="showAssignCategoryDialog" :open.sync="showAssignCategoryDialog" :category="selectedCategory" @assigned="reset")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import ManageServiceItem from '@/components/clinic/manage/ManageServiceItem.vue'
import CreateCategoryDialog from '@/components/clinic/dialogs/CreateCategoryDialog.vue'
import SortCategoryDialog from '@/components/clinic/dialogs/SortCategoryDialog.vue'
import RenameCategoryDialog from '@/components/clinic/dialogs/RenameCategoryDialog.vue'
import DeleteCategoryDialog from '@/components/clinic/dialogs/DeleteCategoryDialog.vue'
import AssignCategoryServicesDialog from '@/components/clinic/dialogs/AssignCategoryServicesDialog.vue'
import draggable from 'vuedraggable'

export default {
  name: 'ManageServices',
  components: { ManageServiceItem, CreateCategoryDialog, SortCategoryDialog, RenameCategoryDialog, DeleteCategoryDialog, AssignCategoryServicesDialog, draggable },
  data: () => ({
    isFetching: false,
    searchText: '',
    pageSize: 20,
    totalCount: 0,
    currentPage: 1,
    services: [],
    fromServiceId: null,
    drag: false,
    params: {},
    userCategories: [],
    categoryTab: 0,
    selectedCategory: null,
    showSearchField: false,
    showCreateDialog: false,
    showSortDialog: false,
    showRenameDialog: false,
    showDeleteDialog: false,
    showAssignCategoryDialog: false
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    categories () {
      const defaultCategories = [{ name: 'All', isDefault: true }, { name: 'Uncategorized', isUncategoried: true, isDefault: true }]
      return [...defaultCategories, ...this.userCategories]
    }
  },
  mounted () {
    this.fetchServices()
    this.fetchCategories()
    this.selectedCategory = this.categories[this.categoryTab]
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchServices () {
      this.isFetching = true
      const params = {
        page_size: -1,
        search: this.searchText || '',
        expand: 'categories'
      }
      if (this.selectedCategory?.isUncategoried) {
        params['categorized'] = false
      } else if (this.selectedCategory?.id) {
        params['categories'] = this.selectedCategory.id
      }
      this.$axios.get(`/clinics/${this.company.slug}/services`, { params })
        .then(resp => {
          this.services = []
          if (resp.data && resp.data.results) {
            this.services = resp.data.results.map(m => {
              let enabled = false
              if (m.status === 'published') {
                enabled = true
              }
              return { ...m, enabled: enabled }
            })
            this.totalCount = resp.data.count
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchCategories () {
      this.$axios.get(`/product_categories?company=${this.company.id}&page_size=-1`)
        .then(resp => {
          if (resp?.data.results) {
            this.userCategories = resp.data.results
          }
        })
    },
    queryChanged: debounce(function () {
      this.fetchServices()
    }, 750),
    addNewService () {
      this.$router.push('/admin/clinic/manage/service')
    },
    checkMove (e) {
      let fromService = this.services[e.draggedContext.index]
      let toService = this.services[e.draggedContext.futureIndex]
      this.params = {
        position: toService.order,
      }
      this.fromServiceId = fromService.id
    },
    endMove () {
      this.drag = false
      if (!this.fromServiceId) {
        return
      }
      if (this.fromServiceId === this.params.service) {
        return 
      }
      this.$axios.post(`/clinics/${this.company.slug}/services/${this.fromServiceId}/move/`, this.params).catch(() => {})
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    tabChanged () {
      if (this.categories) {
        this.selectedCategory = this.categories[this.categoryTab]
      }
      this.services = []
      this.fetchServices()
    },
    createNewCategory () {
      this.showCreateDialog = true
    },
    sortCategory () {
      this.showSortDialog = true
    },
    reset () {
      this.categoryTab = 0
      this.fetchCategories()
      this.tabChanged()
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}

.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}

::v-deep .v-slide-group__prev, .v-slide-group__next {
  flex: 0 1 16px !important;
  min-width: 16px !important;
}

::v-deep .v-tab {
  min-width: 24px !important;
}
</style>